import React, { useEffect, useState } from "react";
import { Button, DatePicker, Dropdown, Input, Select, Table, Tag, Tooltip } from "antd";
import { MoreOutlined, SearchOutlined, WarningOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as dayjs from "dayjs";
import { formatPriceByCurrency, formatPriceUsd } from "../../../../functions/formatters";
import { findDuplicatePaymentMatches } from "../../../../functions/duplicatePayments";
import urls from "../../../../urls";
import { usePurchaseOrderInvoices, usePurchaseOrderPayments } from "../../../../data/hooks";
import {
  ListCard,
  Num,
  PageHeader,
  RecordLink,
  StatCards,
} from "../../../../components/shared-components/purchase";

const { RangePicker } = DatePicker;

const invoiceStatusColors = {
  draft: "default",
  submitted: "processing",
  approved: "blue",
  partially_paid: "orange",
  paid: "green",
  disputed: "red",
};

const invoiceStatusLabels = {
  draft: "Concept",
  submitted: "Ingediend",
  approved: "Goedgekeurd",
  partially_paid: "Deels betaald",
  paid: "Betaald",
  disputed: "Betwist",
};

const PurchaseOrderInvoicesList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [supplierFilter, setSupplierFilter] = useState(searchParams.get("supplier") || null);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || null);
  const [typeFilter, setTypeFilter] = useState(searchParams.get("type") || null);
  const [searchText, setSearchText] = useState(searchParams.get("q") || "");
  const [dateRange, setDateRange] = useState(null);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page")) || 1);

  // Load filters from localStorage on component mount
  useEffect(() => {
    try {
      const savedFilters = localStorage.getItem("purchaseOrderInvoicesFilters");
      if (savedFilters) {
        const filters = JSON.parse(savedFilters);
        if (filters.supplierFilter) setSupplierFilter(filters.supplierFilter);
        if (filters.statusFilter) setStatusFilter(filters.statusFilter);
        if (filters.typeFilter) setTypeFilter(filters.typeFilter);
        if (filters.searchText) setSearchText(filters.searchText);
        if (filters.currentPage) setCurrentPage(filters.currentPage);
      }
    } catch (error) {
      console.warn("Failed to load invoice filters from localStorage:", error);
    }
  }, []);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(
        "purchaseOrderInvoicesFilters",
        JSON.stringify({ supplierFilter, statusFilter, typeFilter, searchText, currentPage }),
      );
    } catch (error) {
      console.warn("Failed to save invoice filters to localStorage:", error);
    }
  }, [supplierFilter, statusFilter, typeFilter, searchText, currentPage]);

  // Use hook to fetch invoices
  const { invoices, loading } = usePurchaseOrderInvoices();

  // Own + linked-proforma payments, for the issue indicator's payments-drift check
  const { payments: allPayments } = usePurchaseOrderPayments({ invoiceIds: invoices.map((inv) => inv.id) });

  const updateSearchParams = (updates) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    setSearchParams(params, { replace: true });
  };

  // Get unique suppliers for filter, sorted alphabetically
  const suppliers = [
    ...new Map(invoices.map((inv) => [inv.supplierId, { id: inv.supplierId, name: inv.supplierName }])).values(),
  ].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  // Apply filters
  const filteredInvoices = invoices.filter((inv) => {
    if (supplierFilter && inv.supplierId !== supplierFilter) return false;
    if (statusFilter && (inv.status || "submitted") !== statusFilter) return false;
    if (typeFilter && inv.type !== typeFilter) return false;
    if (searchText) {
      const search = searchText.toLowerCase();
      const matchesNumber = inv.invoiceNumber?.toLowerCase().includes(search);
      const matchesShipment = inv.shipmentNumber?.toLowerCase().includes(search);
      const matchesOrder = inv.purchaseOrderNumber?.toLowerCase().includes(search);
      if (!matchesNumber && !matchesShipment && !matchesOrder) return false;
    }
    if (dateRange && dateRange[0] && dateRange[1]) {
      const invoiceDate = inv.invoiceDate ? dayjs.unix(inv.invoiceDate) : null;
      if (!invoiceDate) return false;
      if (invoiceDate.isBefore(dateRange[0], "day") || invoiceDate.isAfter(dateRange[1], "day")) {
        return false;
      }
    }
    return true;
  });

  // Summary stats
  const totalAmount = filteredInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
  const totalPaid = filteredInvoices.reduce((sum, inv) => sum + (inv.totalPaid || 0), 0);
  const totalOutstanding = filteredInvoices.reduce((sum, inv) => sum + (inv.amountDue ?? inv.totalAmount ?? 0), 0);
  const outstandingCount = filteredInvoices.filter((inv) => (inv.amountDue ?? inv.totalAmount ?? 0) > 0).length;
  const proformaCount = filteredInvoices.filter((inv) => inv.type === "proforma").length;

  // Een pro forma is de aanbetaling; hij is pas "verrekend" als hij aan een
  // definitieve factuur hangt. Niet-verrekende aanbetalingen zijn geld dat al
  // betaald is maar nog nergens tegenover staat - vandaar een eigen kerncijfer.
  const unsettledProformas = filteredInvoices.filter((inv) => inv.type === "proforma" && !inv.linkedFinalInvoiceId);
  const unsettledProformaAmount = unsettledProformas.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);

  const invoicesById = new Map(invoices.map((inv) => [inv.id, inv]));

  // Flags rows where stored totals don't add up, so discrepancies can be spotted from the overview
  // without opening every invoice. Payments-drift only sums this invoice's own + linked-proforma
  // payments (not legacy purchaseOrders.payments) - an invoice still settled the legacy way will
  // show a drift here even though its stored totalPaid is correct.
  const getInvoiceIssues = (record) => {
    const round2 = (n) => Math.round((n || 0) * 100) / 100;
    const totalAmount = round2(record.totalAmount);
    const totalPaid = round2(record.totalPaid);
    const amountDue = round2(record.amountDue ?? record.totalAmount ?? 0);

    const issues = [];
    // Proforma invoices are created as a lump totalAmount with no line items (subtotals are
    // always 0 by design), so this check only makes sense for final invoices.
    if (record.type === "final") {
      const linesTotal = round2(
        (record.subtotalProducts || 0) + (record.subtotalAdditionalCosts || 0) + (record.subtotalDiscounts || 0),
      );
      if (Math.abs(totalAmount - linesTotal) >= 0.01) {
        issues.push(
          `Totaalbedrag (${formatPriceByCurrency(totalAmount, record.currency)}) komt niet overeen met som van regels (${formatPriceByCurrency(linesTotal, record.currency)})`,
        );
      }
    }
    if (Math.abs(amountDue - round2(totalAmount - totalPaid)) >= 0.01) {
      issues.push(
        `Openstaand (${formatPriceByCurrency(amountDue, record.currency)}) komt niet overeen met totaal minus betaald (${formatPriceByCurrency(round2(totalAmount - totalPaid), record.currency)})`,
      );
    }

    const relevantPayments = allPayments.filter(
      (p) =>
        p.invoiceId === record.id ||
        (record.type === "final" && record.linkedProformaInvoiceIds?.includes(p.invoiceId)),
    );
    const paymentsSum = round2(relevantPayments.reduce((sum, p) => sum + (p.amount || 0), 0));
    if (Math.abs(paymentsSum - totalPaid) >= 0.01) {
      issues.push(
        `Som betalingen (${formatPriceByCurrency(paymentsSum, record.currency)}) komt niet overeen met betaald (${formatPriceByCurrency(totalPaid, record.currency)})`,
      );
    }

    // A payment without its own exchangeRate is silently excluded from the weighted
    // exchange-rate calculation on the shipment's "Verwerk ontvangst" screen - most often
    // seen on payments migrated from the old order-level system, which never had one.
    if ((record.currency || "USD") !== "EUR") {
      const paymentsMissingRate = relevantPayments.filter((p) => !p.exchangeRate);
      if (paymentsMissingRate.length > 0) {
        issues.push(
          `${paymentsMissingRate.length} betaling${paymentsMissingRate.length > 1 ? "en" : ""} zonder wisselkoers - telt niet mee in de gewogen-koers-berekening`,
        );
      }
    }

    // Same-supplier payments (any of their invoices, including this one) that possibly
    // duplicate one of this invoice's own payments - e.g. the same deposit entered on both
    // the proforma and the final invoice.
    const ownPayments = allPayments.filter((p) => p.invoiceId === record.id);
    const supplierPayments = allPayments.filter((p) => invoicesById.get(p.invoiceId)?.supplierId === record.supplierId);
    const duplicateMatches = ownPayments.flatMap((p) => findDuplicatePaymentMatches(p, supplierPayments, invoicesById));
    if (duplicateMatches.length > 0) {
      const sameInvoiceDuplicate = duplicateMatches.some((m) => m.invoice.id === record.id);
      const otherInvoiceLabels = [
        ...new Set(
          duplicateMatches.filter((m) => m.invoice.id !== record.id).map((m) => m.invoice.invoiceNumber || m.invoice.id),
        ),
      ];
      const parts = [];
      if (sameInvoiceDuplicate) parts.push("twee keer dezelfde betaling op deze factuur");
      if (otherInvoiceLabels.length > 0) parts.push(`ook geregistreerd bij factuur: ${otherInvoiceLabels.join(", ")}`);
      issues.push(`Mogelijk dubbele betaling - ${parts.join("; ")}`);
    }

    return issues;
  };

  const columns = [
    {
      title: "Factuur",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      sorter: (a, b) => (a.invoiceNumber || "").localeCompare(b.invoiceNumber || ""),
      render: (value, record) => {
        const issues = getInvoiceIssues(record);
        return (
          // Pro forma / definitief stond als aparte kolom; als onderschrift bij het
          // nummer kost het geen kolombreedte en lees je het samen met het nummer.
          <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
              {issues.length > 0 ? (
                <Tooltip
                  title={
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      {issues.map((issue, i) => (
                        <li key={i}>{issue}</li>
                      ))}
                    </ul>
                  }
                >
                  <WarningOutlined style={{ color: "#fa8c16", fontSize: 14, cursor: "help", flex: "none" }} />
                </Tooltip>
              ) : null}
              <RecordLink to={`${urls.purchaseOrderInvoiceDetail}${record.id}`}>{value || "—"}</RecordLink>
            </div>
            <span
              style={{
                fontSize: 10.5,
                letterSpacing: "0.09em",
                textTransform: "uppercase",
                color: record.type === "proforma" ? "#96601A" : "rgba(0, 0, 0, 0.4)",
              }}
            >
              {record.type === "proforma" ? "Pro forma" : "Definitief"}
            </span>
          </div>
        );
      },
    },
    {
      title: t("supplier.supplier"),
      dataIndex: "supplierName",
      key: "supplierName",
      render: (name) => name || "-",
    },
    {
      title: "Gekoppeld aan",
      key: "reference",
      render: (_, record) => {
        // Een pro forma hangt aan de inkooporder, een definitieve factuur aan de
        // zending. Eén kolom met het nummer + waaraan het hangt, in plaats van
        // twee gestapelde tags.
        const isProforma = record.type === "proforma";
        const to = isProforma
          ? record.purchaseOrderId
            ? `${urls.purchaseOrdersList}/${record.purchaseOrderId}`
            : null
          : record.shipmentId
            ? `${urls.purchaseOrderShipmentDetail}${record.shipmentId}`
            : null;
        const label = isProforma
          ? record.purchaseOrderNumber || (record.purchaseOrderId ? `${record.purchaseOrderId.substring(0, 8)}…` : null)
          : record.shipmentNumber;

        if (!label) return <span style={{ color: "rgba(0, 0, 0, 0.25)" }}>—</span>;
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            <RecordLink to={to} size={12.5}>
              {label}
            </RecordLink>
            <span style={{ fontSize: 11.5, color: "rgba(0, 0, 0, 0.4)" }}>
              {isProforma ? "Inkooporder" : "Zending"}
            </span>
          </div>
        );
      },
    },
    {
      title: "Verrekening",
      key: "settlement",
      render: (_, record) => {
        if (record.type === "proforma") {
          if (record.linkedFinalInvoiceId) {
            const finalInvoice = invoices.find((inv) => inv.id === record.linkedFinalInvoiceId);
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0, fontSize: 12.5 }}>
                <RecordLink to={`${urls.purchaseOrderInvoiceDetail}${record.linkedFinalInvoiceId}`} size={12.5}>
                  {finalInvoice?.invoiceNumber || record.linkedFinalInvoiceId.substring(0, 8)}
                </RecordLink>
                <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>verrekend</span>
              </div>
            );
          }
          return <span style={{ fontSize: 12.5, color: "#96601A" }}>Nog niet verrekend</span>;
        }
        const count = record.linkedProformaInvoiceIds?.length || 0;
        return (
          <span style={{ fontSize: 12.5, color: count ? "rgba(0, 0, 0, 0.45)" : "rgba(0, 0, 0, 0.35)" }}>
            {count > 0 ? `${count} pro forma${count > 1 ? "'s" : ""} verrekend` : "Geen aanbetaling gekoppeld"}
          </span>
        );
      },
    },
    {
      title: t("invoice.invoice_date"),
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      sorter: (a, b) => (a.invoiceDate || 0) - (b.invoiceDate || 0),
      render: (sec) => <Num>{sec ? dayjs.unix(sec).format("DD-MM-YYYY") : "-"}</Num>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={invoiceStatusColors[status] || "default"}>
          {invoiceStatusLabels[status] || status || "Ingediend"}
        </Tag>
      ),
    },
    {
      title: "Totaal / openstaand",
      key: "amounts",
      align: "right",
      sorter: (a, b) => (a.totalAmount || 0) - (b.totalAmount || 0),
      render: (_, record) => {
        const due = record.amountDue ?? record.totalAmount ?? 0;
        return (
          // Was drie regels (totaal / betaald / openstaand). Betaald is af te
          // leiden uit de andere twee; twee regels scheelt een derde rijhoogte.
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
            <Num>{formatPriceByCurrency(record.totalAmount, record.currency)}</Num>
            <Num size={11.5} style={{ color: due > 0 ? "#9E2F2F" : "rgba(0, 0, 0, 0.4)" }}>
              {due > 0 ? `open ${formatPriceByCurrency(due, record.currency)}` : "volledig betaald"}
            </Num>
          </div>
        );
      },
    },
    {
      key: "actions",
      width: 44,
      render: (_, record) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "open", label: "Factuur openen" },
              record.type === "proforma" && record.purchaseOrderId
                ? { key: `po:${record.purchaseOrderId}`, label: `Inkooporder ${record.purchaseOrderNumber || ""}`.trim() }
                : null,
              record.type !== "proforma" && record.shipmentId
                ? { key: `sh:${record.shipmentId}`, label: `Zending ${record.shipmentNumber || ""}`.trim() }
                : null,
            ].filter(Boolean),
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();
              if (key === "open") navigate(`${urls.purchaseOrderInvoiceDetail}${record.id}`);
              else if (key.startsWith("po:")) navigate(`${urls.purchaseOrdersList}/${key.slice(3)}`);
              else if (key.startsWith("sh:")) navigate(`${urls.purchaseOrderShipmentDetail}${key.slice(3)}`);
            },
          }}
        >
          <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t("purchase_order_invoices")}
        subtitle={`${filteredInvoices.length} facturen · ${proformaCount} pro forma · ${outstandingCount} openstaand`}
      />

      <StatCards
        items={[
          {
            label: "Openstaand",
            value: formatPriceUsd(totalOutstanding),
            hint: `over ${outstandingCount} ${outstandingCount === 1 ? "factuur" : "facturen"}`,
            tone: totalOutstanding > 0 ? "danger" : "default",
          },
          {
            label: "Betaald",
            value: formatPriceUsd(totalPaid),
            hint: totalAmount ? `${Math.round((totalPaid / totalAmount) * 100)}% van het totaal` : undefined,
          },
          {
            label: "Totaalbedrag",
            value: formatPriceUsd(totalAmount),
            hint: `${filteredInvoices.length} facturen`,
          },
          {
            label: "Pro forma niet verrekend",
            value: formatPriceUsd(unsettledProformaAmount),
            hint: `${unsettledProformas.length} aanbetaling${unsettledProformas.length === 1 ? "" : "en"}`,
            tone: unsettledProformaAmount > 0 ? "warning" : "default",
          },
        ]}
      />

      <ListCard
        filters={
          <>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: "rgba(0, 0, 0, 0.35)" }} />}
              placeholder="Factuurnummer, order of zending"
              style={{ width: 260 }}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(1);
                updateSearchParams({ q: e.target.value || null, page: null });
              }}
            />
            <Select
              allowClear
              placeholder="Alle soorten"
              style={{ minWidth: 150 }}
              value={typeFilter}
              onChange={(v) => {
                setTypeFilter(v);
                setCurrentPage(1);
                updateSearchParams({ type: v, page: null });
              }}
              options={[
                { value: "proforma", label: "Pro forma" },
                { value: "final", label: "Definitief" },
              ]}
            />
            <Select
              allowClear
              placeholder={t("supplier.supplier")}
              style={{ minWidth: 200 }}
              value={supplierFilter}
              onChange={(v) => {
                setSupplierFilter(v);
                setCurrentPage(1);
                updateSearchParams({ supplier: v, page: null });
              }}
              options={suppliers.map((s) => ({ value: s.id, label: s.name || s.id }))}
            />
            <Select
              allowClear
              placeholder="Alle statussen"
              style={{ minWidth: 180 }}
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v);
                setCurrentPage(1);
                updateSearchParams({ status: v, page: null });
              }}
              options={Object.entries(invoiceStatusLabels).map(([value, label]) => ({ value, label }))}
            />
            <RangePicker format="DD-MM-YYYY" placeholder={["Vanaf", "Tot"]} onChange={(dates) => setDateRange(dates)} />
          </>
        }
        footnote="Een pro forma is pas verrekend als hij aan een definitieve factuur hangt. Niet-verrekende aanbetalingen blijven hier zichtbaar."
      >
        <Table
          loading={loading}
          dataSource={filteredInvoices}
          columns={columns}
          rowKey="id"
          scroll={{ x: true }}
          onRow={(record) => ({
            onClick: () => navigate(`${urls.purchaseOrderInvoiceDetail}${record.id}`),
            style: { cursor: "pointer" },
          })}
          pagination={{
            current: currentPage,
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} van ${total}`,
            onChange: (page) => {
              setCurrentPage(page);
              updateSearchParams({ page: String(page) });
            },
          }}
        />
      </ListCard>
    </>
  );
};

export default PurchaseOrderInvoicesList;
